/**
 * First-run onboarding: a live setup checklist + a welcome modal.
 *
 * The checklist reflects real account state — 2FA, Alpaca connection, and whether
 * the bot is configured/running — so items tick off on their own as the user
 * finishes each one. The welcome modal shows once per browser (per user) until it's
 * dismissed or every step is done; it can be reopened from the Help page.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { botAPI } from '../services/api';
import { CheckCircle2, Circle, ArrowRight, Sparkles, X, ShieldCheck, Link2, Sliders, Play, BookOpen } from 'lucide-react';

const dismissKey = (uid) => `paeru_ob_dismissed_${uid || 'anon'}`;
const botSeenKey = (uid) => `paeru_ob_botcfg_${uid || 'anon'}`;

/** Call when the user reaches Bot Control, so "Set bot preferences" can tick off. */
export function markBotControlVisited(uid) {
  try { localStorage.setItem(botSeenKey(uid), '1'); } catch (e) { /* ignore */ }
}

/** Live onboarding steps + completion, derived from real account state. */
export function useOnboardingSteps() {
  const { user, hasAlpacaKeys } = useAuth();
  const [botRunning, setBotRunning] = useState(false);
  const uid = user?.id;

  useEffect(() => {
    let alive = true;
    botAPI.getStatus().then((r) => { if (alive) setBotRunning(!!r.data?.isRunning); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  let botSeen = false;
  try { botSeen = localStorage.getItem(botSeenKey(uid)) === '1'; } catch (e) { /* ignore */ }

  const steps = [
    { id: '2fa', icon: ShieldCheck, label: 'Secure your account', desc: 'Two-factor authentication', done: !!user?.mfaEnabled, to: '/settings', cta: 'Settings' },
    { id: 'alpaca', icon: Link2, label: 'Connect your Alpaca account', desc: 'Link a free paper-trading account', done: !!hasAlpacaKeys, to: '/settings', cta: 'Connect' },
    { id: 'botcfg', icon: Sliders, label: 'Set your bot preferences', desc: 'Positions, daily trades, strategy', done: botSeen || botRunning, to: '/admin', cta: 'Configure' },
    { id: 'start', icon: Play, label: 'Start the bot', desc: 'Let it scan and trade for you', done: botRunning, to: '/admin', cta: 'Start' },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  return { steps, doneCount, allDone: doneCount === steps.length };
}

/** The checklist itself — reused in the welcome modal and on the Help page. */
export function OnboardingChecklist({ onNavigate }) {
  const navigate = useNavigate();
  const { steps, doneCount } = useOnboardingSteps();

  const go = (to) => { if (onNavigate) onNavigate(); navigate(to); };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-text-secondary">Setup progress</span>
        <span className="text-sm font-mono text-text-muted">{doneCount}/{steps.length}</span>
      </div>
      <div className="h-1.5 rounded-full bg-bg-tertiary overflow-hidden mb-4">
        <div className="h-full bg-accent-green transition-all duration-500" style={{ width: `${(doneCount / steps.length) * 100}%` }} />
      </div>
      <div className="flex flex-col gap-2">
        {steps.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.id} className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${s.done ? 'border-accent-green/25 bg-accent-green/5' : 'border-border-color bg-bg-tertiary/40'}`}>
              {s.done
                ? <CheckCircle2 className="w-5 h-5 text-accent-green flex-none" />
                : <Circle className="w-5 h-5 text-text-muted flex-none" />}
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium flex items-center gap-2 ${s.done ? 'text-text-secondary line-through decoration-text-muted/40' : 'text-text-primary'}`}>
                  <Icon className="w-3.5 h-3.5 opacity-70" /> {s.label}
                </div>
                <div className="text-xs text-text-muted">{s.desc}</div>
              </div>
              {!s.done && (
                <button onClick={() => go(s.to)} className="btn-secondary px-3 py-1.5 text-xs flex items-center gap-1 flex-none">
                  {s.cta} <ArrowRight className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** First-run welcome modal. Self-manages visibility; mount once (e.g. in AppShell). */
export function WelcomeModal() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { allDone } = useOnboardingSteps();
  const uid = user?.id;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!uid) return;
    let dismissed = false;
    try { dismissed = localStorage.getItem(dismissKey(uid)) === '1'; } catch (e) { /* ignore */ }
    // Show on first run: not dismissed and not already fully set up.
    setOpen(!dismissed && !allDone);
  }, [uid, allDone]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(dismissKey(uid), '1'); } catch (e) { /* ignore */ }
    setOpen(false);
  }, [uid]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={dismiss}>
      <div className="bg-bg-secondary border border-border-color rounded-2xl w-full max-w-lg overflow-hidden animate-slide-up" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 pb-4 border-b border-border-color relative">
          <button onClick={dismiss} className="absolute top-4 right-4 text-text-muted hover:text-text-primary" aria-label="Close"><X className="w-5 h-5" /></button>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-9 h-9 rounded-xl grid place-items-center flex-none" style={{ background: 'linear-gradient(140deg,#E7B24C,#C0902E)' }}>
              <Sparkles className="w-5 h-5" style={{ color: '#2b1c04' }} />
            </div>
            <h2 className="text-xl font-semibold">Welcome to Paeru{user?.username ? `, ${user.username}` : ''}</h2>
          </div>
          <p className="text-text-secondary text-sm">Let's get you trading — it's all paper money, so there's zero risk. Four quick steps:</p>
        </div>
        <div className="p-6">
          <OnboardingChecklist onNavigate={() => setOpen(false)} />
        </div>
        <div className="p-4 border-t border-border-color flex items-center justify-between gap-3">
          <button onClick={() => { setOpen(false); navigate('/help'); }} className="btn-secondary flex items-center gap-2 text-sm">
            <BookOpen className="w-4 h-4" /> Open full guide
          </button>
          <button onClick={dismiss} className="btn-primary text-sm">Got it</button>
        </div>
      </div>
    </div>
  );
}

/** Clears the dismissed flag so the welcome modal shows again (used from Help). */
export function replayWelcome(uid) {
  try { localStorage.removeItem(dismissKey(uid)); } catch (e) { /* ignore */ }
}

/** True while the first-run welcome flow is still active (not yet dismissed). Lets
 *  other screens (e.g. the Dashboard's own connect prompt) defer so modals don't stack. */
export function isOnboardingActive(uid) {
  try { return localStorage.getItem(dismissKey(uid)) !== '1'; } catch (e) { return false; }
}
